import {
  getSuggestions,
  addResultsToCache,
  cleanUpCache,
  getDataFromCache,
  queryValidForSearch as cacheQueryValidForSearch,
} from './navigation-search-cache';

import api from '../../core/api';

type SearchResponse = {
  subtext: string;
  text: string;
  type: string;
  linkInfo: { memberId: number; organizationId: number };
  subLinks: {
    isReactRoute: boolean;
    defaultIcon: string;
    linkHost: string;
    linkUrl: string;
    subtext: string;
    text: string;
    type: string;
    linkInfo: {
      memberId: number;
      organizationId: number;
      programId: number;
      seasonId: number;
      seasonParticipantId: number;
      seasonRootDivisionId: number;
    };
  }[];
};

export function queryValidForSearch(query: string) {
  return cacheQueryValidForSearch(query);
}

export default class NavigationSearchServer {
  searchCache: object;
  isLoadingResults: boolean;
  hasLoadingError: boolean;
  lastSearchQuery: string;
  queuedSearchQuery: string;

  constructor() {
    this.searchCache = {};
    this.isLoadingResults = false;
    this.hasLoadingError = false;
    this.lastSearchQuery = null;
    this.queuedSearchQuery = null;
  }

  async getSuggestions(searchQuery: string, searchContext: object): Promise<[any, boolean, boolean]> {
    searchQuery = searchQuery.trim();
    this.searchCache = cleanUpCache(this.searchCache);

    if (queryValidForSearch(searchQuery)) {
      const usedQuery = await this.checkSearch(searchQuery, searchContext);

      return [getSuggestions(this.searchCache, usedQuery, searchContext), this.isLoadingResults, this.hasLoadingError];
    } else {
      this.queuedSearchQuery = null;
      return [[], this.isLoadingResults, this.hasLoadingError];
    }
  }

  async checkSearch(query, searchContext) {
    const [cachedDataset, updatedResults] = getDataFromCache(this.searchCache, query);
    this.searchCache = updatedResults;

    if (!cachedDataset && !this.isLoadingResults) {
      this.lastSearchQuery = query;
      this.queuedSearchQuery = query;
      this.isLoadingResults = true;
      return this.search(this.lastSearchQuery, searchContext);
    } else {
      this.queuedSearchQuery = query;
      return query;
    }
  }

  async search(query, searchContext) {
    try {
      const navigationResults = await api.get<SearchResponse[]>(`search?q=${query}`);
      this.searchCache = addResultsToCache(this.searchCache, query, navigationResults.data);
      this.isLoadingResults = false;
      this.hasLoadingError = false;
      if (this.queuedSearchQuery && query !== this.queuedSearchQuery) {
        return this.checkSearch(this.queuedSearchQuery, searchContext);
      } else {
        return query;
      }
    } catch (e) {
      this.hasLoadingError = true;
      this.isLoadingResults = false;
      return query;
    }
  }
}
